import styled from "styled-components";

import { previewTheme } from "../preview";

/** Colours lifted from the generated receipt so the mock reads as the real thing. */
export const receiptTheme = {
  indigo: "#4f46e5",
  paper: "#ffffff",
  canvas: "#f9f9fa",
  hairline: "#d9d9d9",
  ink: "#0d0d0d",
  body: "#666666",
  footer: "#9da5b1",
};

/**
 * The sheet itself. Everything inside is deliberately tiny — this is a shape
 * preview, not a readable document, so the whole receipt fits without scrolling.
 */
export const StyledReceipt = styled.div`
  background-color: ${receiptTheme.canvas};
  border: 1px solid ${previewTheme.hairline};
  border-radius: 6px;
  box-shadow: 0 2px 10px rgba(23, 20, 63, 0.08);
  min-height: 520px;
  padding: 12px 14px 0;
  display: flex;
  flex-direction: column;
  color: ${receiptTheme.ink};
  font-size: 8px;
  line-height: 1.45;
`;

export const StyledReceiptBody = styled.div`
  flex: 1;
`;

export const StyledReceiptHeading = styled.p`
  && {
    color: ${receiptTheme.indigo};
  }
  font-size: 10px;
  font-weight: 700;
  text-align: center;
  margin: 0 0 3px;
`;

export const StyledReceiptSubheading = styled.p`
  && {
    color: ${receiptTheme.indigo};
  }
  font-size: 8px;
  text-align: center;
  margin: 0 0 2px;
`;

/** The two half-width cards that sit side by side, as in the document. */
export const StyledCardRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-top: 8px;
`;

export const StyledSection = styled.div`
  background-color: ${receiptTheme.paper};
  border: 1px solid ${receiptTheme.hairline};
  border-radius: 6px;
  padding: 8px 10px;
  margin-top: 8px;
`;

/* Sits inside StyledCardRow / StyledMarkedBlock, which already space the cards. */
export const StyledSubsection = styled(StyledSection)`
  margin-top: 0;
`;

export const StyledSectionTitle = styled.p`
  && {
    color: ${receiptTheme.ink};
  }
  font-size: 8.5px;
  font-weight: 700;
  margin: 0 0 6px;
`;

export const StyledDetailLine = styled.p`
  && {
    color: ${receiptTheme.body};
  }
  font-size: 7.5px;
  margin: 0 0 2px;

  strong {
    color: ${receiptTheme.ink};
    font-weight: 700;
  }
`;

/* Label on the left, amount on the right — the receipt's payment row. */
export const StyledAmountRow = styled.div<{
  $accent?: boolean;
  $sub?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
  padding-left: ${({ $sub }) => ($sub ? "10px" : "0")};
  color: ${({ $accent }) =>
    $accent ? receiptTheme.indigo : receiptTheme.body};
  font-size: ${({ $sub }) => ($sub ? "7px" : "7.5px")};
  font-weight: ${({ $accent }) => ($accent ? 700 : 400)};

  span:last-child {
    color: ${({ $accent }) =>
      $accent ? receiptTheme.indigo : receiptTheme.ink};
    font-weight: 700;
    white-space: nowrap;
  }
`;

export const StyledDivider = styled.div`
  border-top: 1px solid ${receiptTheme.hairline};
  margin: 6px 0;
`;

export const StyledInstallmentNote = styled.p`
  && {
    color: ${receiptTheme.indigo};
  }
  font-size: 7px;
  margin: 0 0 4px;
`;

export const StyledMessageText = styled.p`
  && {
    color: ${receiptTheme.body};
  }
  font-size: 7px;
  line-height: 1.5;
  margin: 0;
`;

/* Mirrors the signature line printed above the footer on the real receipt. */
export const StyledSignLine = styled.div`
  width: 50%;
  margin: 16px auto 4px;
  border-top: 1px solid ${receiptTheme.ink};
  padding-top: 2px;
  text-align: center;
  color: ${receiptTheme.body};
  font-size: 7px;
`;

/* White band pinned to the foot of the sheet, matching the fixed PDF footer. */
export const StyledReceiptFooter = styled.div`
  background-color: ${receiptTheme.paper};
  margin: 10px -14px 0;
  padding: 8px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${receiptTheme.footer};
  font-size: 6.5px;
`;

export const StyledFooterLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 20px;
  margin-bottom: 4px;
  border: 1px dashed ${previewTheme.hairline};
  border-radius: 3px;
  color: ${previewTheme.muted};
  font-size: 6px;
  font-weight: 700;
  letter-spacing: 0.06em;
`;
